// src/utils/pdfReport.js
import { jsPDF } from "jspdf";

const CM     = 28.3465;
const PAGE_W = 595.28;
const PAGE_H = 841.89;

/* ── URL → data URL ── */
async function toDataURL(url) {
  const res  = await fetch(url, { mode: "cors" });
  const blob = await res.blob();
  return new Promise((resolve) => {
    const fr = new FileReader();
    fr.onloadend = () => resolve(fr.result);
    fr.readAsDataURL(blob);
  });
}

/* ── data URL → { data, aspectRatio } with rounded corners ── */
async function roundDataURL(dataUrl) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const { naturalWidth: w, naturalHeight: h } = img;
      const r = Math.min(w, h) * 0.06; // ~6% → belirgin oval köşe
      const canvas = document.createElement("canvas");
      canvas.width  = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");

      // Transparan başlat (köşelerin şablonun arkasına bakması için PNG kullanıyoruz)
      ctx.clearRect(0, 0, w, h);
      ctx.beginPath();
      ctx.moveTo(r, 0);
      ctx.lineTo(w - r, 0);
      ctx.quadraticCurveTo(w, 0,  w, r);
      ctx.lineTo(w, h - r);
      ctx.quadraticCurveTo(w, h,  w - r, h);
      ctx.lineTo(r, h);
      ctx.quadraticCurveTo(0, h,  0, h - r);
      ctx.lineTo(0, r);
      ctx.quadraticCurveTo(0, 0,  r, 0);
      ctx.closePath();
      ctx.clip();
      ctx.drawImage(img, 0, 0);

      // PNG: köşeler gerçekten transparan kalır (JPEG kullanırsak siyah olur)
      resolve({
        data: canvas.toDataURL("image/png"),
        aspectRatio: w / h,
      });
    };
    img.src = dataUrl;
  });
}

/* ── full-page PNG background ── */
function drawBg(doc, bgData) {
  doc.addImage(bgData, "PNG", 0, 0, PAGE_W, PAGE_H);
}

/* ── image: dikey yükseklik sabit, yatay en-boy oranına göre ayarlı, ortaya hizalı ── */
function placeImage(doc, imgObj, xCm, yCm, wCm, hCm) {
  if (!imgObj) return;
  const { data, aspectRatio } = imgObj;

  const boxX = xCm * CM;
  const boxY = yCm * CM;
  const boxW = wCm * CM;
  const boxH = hCm * CM;

  // Yüksekliği sabitle, genişliği orijinal oran ile hesapla
  const imgH = boxH;
  const imgW = imgH * aspectRatio;

  // Yatayda ortala (taşarsa box'a sığdır)
  const finalW = Math.min(imgW, boxW);
  const finalH = finalW / aspectRatio;
  const imgX   = boxX + (boxW - finalW) / 2;
  const imgY   = boxY + (boxH - finalH) / 2;

  doc.addImage(data, "PNG", imgX, imgY, finalW, finalH);
}

/* ── centered text inside a cm-coordinate box ── */
function centeredText(doc, text, xCm, yCm, wCm, hCm, { size, color, bold = true, maxWidth }) {
  const x = xCm * CM;
  const y = yCm * CM;
  const w = wCm * CM;
  const h = hCm * CM;

  doc.setFontSize(size);
  doc.setTextColor(...color);
  doc.setFont("helvetica", bold ? "bold" : "normal");

  const opts = { align: "center" };
  if (maxWidth) opts.maxWidth = maxWidth * CM;

  doc.text(String(text), x + w / 2, y + h / 2 + size * 0.35, opts);
}

/* ══════════════════════════════════════════════════════════
   PAGE 1 — COVER
   Hasta adı: w=7.36 h=0.64  x=6.62 y=21.13
══════════════════════════════════════════════════════════ */
function drawCover(doc, bg, { patientName }) {
  drawBg(doc, bg);
  centeredText(
    doc,
    (patientName || "Unknown Patient").toUpperCase(),
    6.62, 21.13, 7.36, 0.64,
    { size: 20, color: [0, 0, 0], bold: true, maxWidth: 7.36 }
  );
}

/* ══════════════════════════════════════════════════════════
   PAGE 2 — PERFORMANCE OVERVIEW
   Overlay : w=17.13 h=13.43  x=2.86  y=4.74
   Sayı    : w=6.54  h=1.94   x=7.58  y=25.32
══════════════════════════════════════════════════════════ */
function drawOverviewPage(doc, bg, { overlayData, graftDisplay }) {
  drawBg(doc, bg);
  placeImage(doc, overlayData, 2.86, 4.74, 17.13, 13.43);
  centeredText(
    doc,
    graftDisplay,
    7.58, 25.32, 6.54, 1.94,
    { size: 18, color: [15, 15, 15], bold: true }
  );
}

/* ══════════════════════════════════════════════════════════
   PAGE 3 — DENSITY MAP
   Heatmap : w=17.49 h=14.32  x=2.78  y=9.89
   High    : w=10.95 h=1.7    x=9.04  y=24.65
   Low     : w=10.95 h=1.7    x=9.04  y=26.77
══════════════════════════════════════════════════════════ */
function drawDensityPage(doc, bg, { heatmapData, highDensityPercent, lowDensityPercent }) {
  drawBg(doc, bg);
  placeImage(doc, heatmapData, 2.78, 9.89, 17.49, 14.32);

  const fmt = (v) => (v != null ? `${Number(v).toFixed(1)}%` : "—");

  centeredText(doc, fmt(highDensityPercent), 9.04, 24.65, 10.95, 1.7, { size: 15, color: [15, 15, 15], bold: true });
  centeredText(doc, fmt(lowDensityPercent),  9.04, 26.77, 10.95, 1.7, { size: 15, color: [15, 15, 15], bold: true });
}

/* ══════════════════════════════════════════════════════════
   EXPORT
══════════════════════════════════════════════════════════ */
export async function buildPdfReport({
  patientName,
  analysisDate,
  detectedCount,
  estimatedRange,
  surgeonCount,
  highDensityPercent,
  lowDensityPercent,
  notes,
  overlayUrl,
  heatmapUrl,
  filename = "GraftCounter_Report.pdf",
}) {
  // 1. Tüm kaynakları paralel yükle
  const [bg1, bg2, bg3, overlayRaw, heatmapRaw] = await Promise.all([
    toDataURL("/report/1.png"),
    toDataURL("/report/2.png"),
    toDataURL("/report/3.png"),
    overlayUrl ? toDataURL(overlayUrl) : Promise.resolve(null),
    heatmapUrl ? toDataURL(heatmapUrl) : Promise.resolve(null),
  ]);

  // 2. Rounded corners uygula
  const [overlayData, heatmapData] = await Promise.all([
    overlayRaw ? roundDataURL(overlayRaw) : Promise.resolve(null),
    heatmapRaw ? roundDataURL(heatmapRaw) : Promise.resolve(null),
  ]);

  // 3. Graft sayısı: cerrahın girdiği > estimatedRange > detectedCount
  const hasSurgeon = surgeonCount && String(surgeonCount).trim() && surgeonCount !== "—";
  const graftDisplay = hasSurgeon
    ? String(surgeonCount).trim()
    : (estimatedRange && estimatedRange !== "—")
      ? estimatedRange
      : detectedCount || "—";

  // 4. PDF oluştur
  const doc = new jsPDF({ unit: "pt", format: "a4", orientation: "portrait", compress: true });

  drawCover(doc, bg1, { patientName });

  doc.addPage("a4");
  drawOverviewPage(doc, bg2, { overlayData, graftDisplay });

  doc.addPage("a4");
  drawDensityPage(doc, bg3, { heatmapData, highDensityPercent, lowDensityPercent });

  doc.save(filename);
}
